import { Controller } from "@hotwired/stimulus"

const DESKTOP_HOVER_MEDIA_QUERY = "(hover: hover) and (pointer: fine)"
const CLOSE_DELAY_MS = 100
const SAFE_ZONE_PADDING_PX = 8

export default class extends Controller {
  static targets = ["summary", "panel"]

  connect() {
    this.pointerClientX = null
    this.pointerClientY = null
    this.desktopHoverQuery = window.matchMedia(DESKTOP_HOVER_MEDIA_QUERY)
    this.handleDesktopHoverChange = this.handleDesktopHoverChange.bind(this)
    this.handlePointerMove = this.handlePointerMove.bind(this)
    this.handleToggle = this.handleToggle.bind(this)
    this.desktopHoverQuery.addEventListener("change", this.handleDesktopHoverChange)
    window.addEventListener("pointermove", this.handlePointerMove)
    this.element.addEventListener("toggle", this.handleToggle)
    this.handleDesktopHoverChange()
  }

  disconnect() {
    this.clearCloseTimeout()
    this.desktopHoverQuery?.removeEventListener("change", this.handleDesktopHoverChange)
    window.removeEventListener("pointermove", this.handlePointerMove)
    this.element.removeEventListener("toggle", this.handleToggle)
  }

  open() {
    if (!this.isDesktopHoverMode()) return

    this.clearCloseTimeout()
    this.closeOtherMenus()
    this.element.open = true
  }

  close() {
    if (!this.isDesktopHoverMode()) return

    this.scheduleCloseIfPointerStaysOutside()
  }

  toggleFromClick(event) {
    if (!this.isDesktopHoverMode()) return
    if (event.detail === 0) return

    event.preventDefault()
    this.open()
  }

  handleToggle() {
    if (this.element.open) this.closeOtherMenus()
  }

  closeOtherMenus() {
    this.element.closest("nav")?.querySelectorAll('details[data-controller~="menu-dropdown"][open]').forEach(menu => {
      if (menu !== this.element) menu.open = false
    })
  }

  handleDesktopHoverChange() {
    if (this.isDesktopHoverMode()) return

    this.clearCloseTimeout()
    this.element.open = false
  }

  handlePointerMove(event) {
    this.pointerClientX = event.clientX
    this.pointerClientY = event.clientY

    if (!this.isDesktopHoverMode() || !this.element.open) return

    if (this.pointerIsWithinSafeZone())
      this.clearCloseTimeout()
    else
      this.scheduleCloseIfPointerStaysOutside()
  }

  isDesktopHoverMode() {
    return this.desktopHoverQuery?.matches
  }

  clearCloseTimeout() {
    if (!this.closeTimeoutId) return

    window.clearTimeout(this.closeTimeoutId)
    this.closeTimeoutId = null
  }

  scheduleCloseIfPointerStaysOutside() {
    if (this.closeTimeoutId) return

    this.closeTimeoutId = window.setTimeout(() => {
      if (this.pointerIsWithinSafeZone()) {
        this.closeTimeoutId = null
        return
      }

      this.element.open = false
      this.closeTimeoutId = null
    }, CLOSE_DELAY_MS)
  }

  pointerIsWithinSafeZone() {
    if (this.pointerClientX == null || this.pointerClientY == null) return false

    const hoveredElement = document.elementFromPoint(this.pointerClientX, this.pointerClientY)
    if (hoveredElement && this.element.contains(hoveredElement)) return true

    if (!this.hasSummaryTarget || !this.hasPanelTarget || !this.element.open) return false

    const summaryRect = this.summaryTarget.getBoundingClientRect()
    const panelRect = this.panelTarget.getBoundingClientRect()
    const left = Math.min(summaryRect.left, panelRect.left) - SAFE_ZONE_PADDING_PX
    const right = Math.max(summaryRect.right, panelRect.right) + SAFE_ZONE_PADDING_PX
    const top = Math.min(summaryRect.top, panelRect.top) - SAFE_ZONE_PADDING_PX
    const bottom = Math.max(summaryRect.bottom, panelRect.bottom) + SAFE_ZONE_PADDING_PX

    return this.pointerClientX >= left &&
      this.pointerClientX <= right &&
      this.pointerClientY >= top &&
      this.pointerClientY <= bottom
  }
}
